import "./documentRow.scss";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import newRequest from "../../utils/newRequest";
import { ScaleLoader } from "react-spinners";
import { GoHomeFill } from "react-icons/go";
import { qilloIconMap } from "../../utils/iconMapping";
import { MdArrowCircleRight } from "react-icons/md";
import { CustomButtonSmall } from "../ui/CustomButton";
import { CiImageOn } from "react-icons/ci";

const RiskNotesWidget = () => {
    const { t } = useTranslation();
    const { currentTheme } = useSelector((state) => state.theme);
    const { currentUser } = useSelector((state) => state.user);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [riskNotes, setRiskNotes] = useState([]);

    const fetchRiskNotes = async () => {
        try {
            setIsLoading(true);
            await newRequest
                .post("/uwPolicy/findAll", {
                    result_limit: 3,
                    pl_type: ["broker", "agent"].includes(currentUser?.token_org_type) ? "RiskNote" : "Policy"
                })
                .then((res) => {
                    // console.log(`setRiskNotes ${JSON.stringify(res.data, null, 2)}`);
                    setRiskNotes(res.data);
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching data:", error);
                    setIsLoading(false);
                });
        } catch (err) {
            console.log(err);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRiskNotes();
    }, [currentUser]);

    return (
        <motion.div
            className="document-row"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
        >
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                riskNotes?.length > 0 ?
                    <div className="policies-wrapper">
                        {riskNotes?.map((row) => {
                            const IconComponent = qilloIconMap[row?.pr_icon_xx] || GoHomeFill;
                            return (
                                <div key={row?._id} className="rnCard">
                                    {/* Top row */}
                                    <div className="rnTop">
                                        <div className="rnProduct">
                                            <div className="rnProductIcon">
                                                <IconComponent className="product-icon" />
                                            </div>
                                            <div className="rnProductMeta">
                                                <div className="rnProductName">{row?.product_name_xx || ""}</div>
                                            </div>
                                        </div>

                                        <div className="rnTopRight">
                                            <span className={`status ${row?.pl_status}`}>{`${t("status")} : ${row?.pl_status}`}</span>
                                            <span className="rnNoteNo">{`${t("noteNo")} ${row?.pl_risk_note_no}`}</span>

                                            <CustomButtonSmall
                                                currentTheme={currentTheme}
                                                iconPosition="end"
                                                endIcon={<MdArrowCircleRight size={20} />}
                                                onClick={() => {
                                                    try {
                                                        navigate(`/uw/policy/${row._id}`, { state: row });
                                                    } catch (error) {
                                                        console.log(error);
                                                    }
                                                }}
                                            >
                                                {t("view")}
                                            </CustomButtonSmall>
                                        </div>
                                    </div>

                                    {/* Bottom row */}
                                    <div className="rnBottom">
                                        <div className="rnCol">
                                            <div className="rnLabel">{t("insured").toUpperCase()}</div>
                                            <div className="rnEntity">
                                                <div className="rnAvatar">
                                                    {row?.insured_details_xx?.ent_photo_url ? (
                                                        <img src={row?.insured_details_xx?.ent_photo_url} alt="" />
                                                    ) : (
                                                        <CiImageOn className="rnAvatarIcon" />
                                                    )}
                                                </div>
                                                <div className="rnEntityMeta">
                                                    <div className="rnEntityName">{row?.insured_details_xx?.ent_name || ""}</div>
                                                    <div className="rnEntitySub">{row?.insured_details_xx?.aent_name_xx}</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="rnCol">
                                            <div className="rnLabel">{t("carrier").toUpperCase()}</div>
                                            <div className="rnEntity">
                                                <div className="rnAvatar">
                                                    {row?.carrier_details_xx?.ent_photo_url ? (
                                                        <img src={row?.carrier_details_xx?.ent_photo_url} alt="" />
                                                    ) : (
                                                        <CiImageOn className="rnAvatarIcon" />
                                                    )}
                                                </div>
                                                <div className="rnEntityMeta">
                                                    <div className="rnEntityName">{row?.carrier_details_xx?.ent_name || ""}</div>
                                                    <div className="rnEntitySub">{row?.carrier_details_xx?.aent_name_xx}</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="rnCol rnLimits">
                                            <div className="rnLabel">{`${t("limits").toUpperCase()} - [${row?.pl_cur_code}]`}</div>
                                            <div className="rnAmount">
                                                <span className="rnAmountLabel">{t("premium")}</span>
                                                <span className="rnAmountValue">{row?.fmt_total_fc_prem}</span>
                                            </div>
                                            <div className="rnAmount">
                                                <span className="rnAmountLabel">{t("commission")}</span>
                                                <span className="rnAmountValue">{row?.fmt_comm_fc}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    :
                    <div className="no-content-wrapper">
                        <span className="main-title">Your last three risk notes will appear here once you create them</span>
                    </div>
            )}
        </motion.div>
    );
};

export default RiskNotesWidget